import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { apiJson } from '@/lib/api';
import type { AdminAuditLogListResponse, AdminUserDetailResponse } from '@/lib/types';

function AdminUserAuditPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hidFromUrl = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? '', [searchStr]);

  const [eventType, setEventType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [householdFilter, setHouseholdFilter] = useState('');

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const auditQuery = useInfiniteQuery({
    queryKey: ['admin', 'audit', id, eventType, entityType, householdFilter],
    queryFn: async ({ pageParam }: { pageParam: number | undefined }) => {
      const p = new URLSearchParams();
      p.set('limit', '50');
      if (pageParam != null) p.set('before_id', String(pageParam));
      if (eventType.trim()) p.set('event_type', eventType.trim());
      if (entityType.trim()) p.set('entity_type', entityType.trim());
      if (householdFilter) p.set('household_id', householdFilter);
      return apiJson<AdminAuditLogListResponse>(
        getToken,
        `/v1/admin/users/${id}/audit-logs?${p.toString()}`,
      );
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more && lastPage.rows.length > 0
        ? lastPage.rows[lastPage.rows.length - 1].id
        : undefined,
  });

  const pages = auditQuery.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.rows);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.has_more;

  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const toggle = useCallback((rowId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const households = userQuery.data?.households ?? [];

  return (
    <div className='space-y-4'>
      <h2 className='text-lg font-semibold text-gray-900'>Audit log</h2>

      <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap gap-3 items-end'>
        <div>
          <label className='block text-xs font-semibold text-gray-600 mb-1'>event_type</label>
          <input
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className='px-2 py-1 border border-gray-300 rounded text-sm w-40'
            placeholder='e.g. expense.created'
          />
        </div>
        <div>
          <label className='block text-xs font-semibold text-gray-600 mb-1'>entity_type</label>
          <input
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className='px-2 py-1 border border-gray-300 rounded text-sm w-32'
            placeholder='expense'
          />
        </div>
        <div>
          <label className='block text-xs font-semibold text-gray-600 mb-1'>household_id</label>
          <select
            value={householdFilter}
            onChange={(e) => setHouseholdFilter(e.target.value)}
            className='px-2 py-1 border border-gray-300 rounded text-sm'
          >
            <option value=''>All (user + households)</option>
            {households.map((h) => (
              <option key={h.household_id} value={String(h.household_id)}>
                #{h.household_id}
              </option>
            ))}
            {hidFromUrl && !households.some((h) => String(h.household_id) === hidFromUrl) ? (
              <option value={hidFromUrl}>#{hidFromUrl} (from URL)</option>
            ) : null}
          </select>
        </div>
      </div>

      <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
        <table className='w-full text-sm'>
          <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
            <tr>
              <th className='px-3 py-2'>Time</th>
              <th className='px-3 py-2'>Event</th>
              <th className='px-3 py-2'>Entity</th>
              <th className='px-3 py-2'>HH</th>
              <th className='px-3 py-2'>Actor</th>
              <th className='px-3 py-2'>IP</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {auditQuery.isLoading ? (
              <tr>
                <td colSpan={6} className='px-4 py-8 text-center text-gray-400'>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className='px-4 py-8 text-center text-gray-400'>
                  No rows.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    className='hover:bg-gray-50 cursor-pointer'
                    onClick={() => toggle(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(r.id);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td className='px-3 py-2 whitespace-nowrap text-gray-600'>
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className='px-3 py-2 font-mono text-xs'>{r.event_type}</td>
                    <td className='px-3 py-2 text-xs'>
                      {r.entity_type} #{r.entity_id}
                    </td>
                    <td className='px-3 py-2 text-xs'>{r.household_id ?? '—'}</td>
                    <td className='px-3 py-2 text-xs'>
                      {r.actor_user_id === id ? (
                        <span className='font-semibold text-primary'>self</span>
                      ) : (
                        r.actor_user_id ?? '—'
                      )}
                    </td>
                    <td className='px-3 py-2 text-xs text-gray-500' title={r.user_agent ?? ''}>
                      {r.ip_address}
                    </td>
                  </tr>
                  {expanded.has(r.id) ? (
                    <tr key={`${r.id}-detail`} className='bg-gray-50'>
                      <td colSpan={6} className='px-3 py-2'>
                        <p className='text-xs font-semibold text-gray-600 mb-1'>event_data</p>
                        <pre className='text-xs overflow-x-auto max-h-48 overflow-y-auto bg-white border border-gray-200 rounded p-2 mb-2'>
                          {JSON.stringify(r.event_data, null, 2)}
                        </pre>
                        {r.changes ? (
                          <>
                            <p className='text-xs font-semibold text-gray-600 mb-1'>changes</p>
                            <pre className='text-xs overflow-x-auto max-h-32 overflow-y-auto bg-white border border-gray-200 rounded p-2'>
                              {JSON.stringify(r.changes, null, 2)}
                            </pre>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore ? (
        <button
          type='button'
          disabled={auditQuery.isFetchingNextPage}
          onClick={() => auditQuery.fetchNextPage()}
          className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md disabled:opacity-50'
        >
          {auditQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/audit')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserAuditPage,
});
